import React from "react"
import ReactJson from "react-json-view"
import {useCopyToClipboard} from "react-use"
import {useToasts} from "react-toast-notifications"
import Button from "../../../components/Button/Button"
import {Modal} from "../../../components/Modals/Modal"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import {getThemeByLink, WhiteLabelThemeByLink} from "../../../resources/whiteLabel"
import Icon from "../../../components/Icon/Icon"

interface ThemeDownloadModalProps {
	themeLink: string
	close: () => void
}

function ThemeDownloadModalContent({themeLink, close}: ThemeDownloadModalProps) {
	const {addToast} = useToasts()
	const [, copyToClipboard] = useCopyToClipboard()
	const themeAsyncResultReq = useAsyncResult(() => getThemeByLink(themeLink))

	const handleCopyToClipboard = (theme: WhiteLabelThemeByLink) => {
		try {
			copyToClipboard(JSON.stringify(theme, null, 2))
			addToast("Theme copied to Clipboard", {appearance: "success"})
		} catch (err) {
			addToast("Error with copying theme to Clipboard", {appearance: "error"})
		}
	}

	return (
		<AsyncResultComponent
			asyncResult={themeAsyncResultReq}
			pendingComponent={
				<div className="loading-label">
					<Icon icon="Icons_loader" size={18} className="mfa-loading spin" />
				</div>
			}
		>
			{({value: theme}) => (
				<>
					<div className="theme-snippet">
						<ReactJson
							style={{fontSize: "12px"}}
							src={theme}
							collapsed={2}
							displayDataTypes={false}
							enableClipboard={false}
						/>
					</div>
					<div className="action-buttons">
						<Button type="button" onClick={close} className="action-button" theme="neutral" variant="outline" rounded>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={() => handleCopyToClipboard(theme)}
							className="action-button"
							theme="neutral"
							rounded
						>
							Copy to Clipboard
						</Button>
					</div>
				</>
			)}
		</AsyncResultComponent>
	)
}

export function ThemeDownloadModal({themeLink, close}: ThemeDownloadModalProps) {
	return (
		<Modal enableClickAway={false} close={close} classname="theme-download-modal-container">
			<div className="theme-download-modal">
				<h2 className="modal-title">Share Theme</h2>
				<p className="modal-description">
					Your theme can only be applied to Live production environment by your Unit CSM. Copy the code snippet and
					forward it to your Unit account manager.
					<br />
					Following the update, your theme will be visible to you in the <strong>Live dashboard.</strong>
				</p>
				<ThemeDownloadModalContent themeLink={themeLink} close={close} />
			</div>
		</Modal>
	)
}
